'use client';

import { memo, useEffect, useId, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useIsLabsFeatureEnabled } from '@frameio/shared/hooks';
import { useThemeName } from '@frameio/vapor';
import { theme } from '@frameio/vapor-theme';
import { getFolderColorMix } from '../../../folder/constants/folderColors';
import { useFolderColor } from '../../../folder/hooks/useFolderColor';
import { ASSET_BG_COLOR } from './styles';

// Folder dimensions and styling constants for different variants
const DIMENSIONS = {
  default: {
    radius: 8,
    tabHeight: 16,
    tabWidth: 80,
    bezierX: 16,
    bezierEndX: 32,
  },
  compact: {
    radius: 5,
    tabHeight: 10,
    tabWidth: 32,
    bezierX: 10,
    bezierEndX: 16,
  },
} as const;

export type AssetCardFolderTabSvgProps = {
  /** ID of the folder asset, used to look up the per-folder color */
  folderId?: string;
  /** Background color of the folder */
  fill?: string;
  /** Border radius of the folder corners */
  radius?: number;
  /** Height of the folder tab from top edge */
  tabHeight?: number;
  /** Width of the folder tab from left edge */
  tabWidth?: number;
  /** Whether to render a simplified overlay version with semi-transparent background */
  isOverlay?: boolean;
  /** CSS class name for styling different path elements */
  className?: string;
  /** Visual style variant of the folder */
  variant?: 'default' | 'compact';
};

/**
 * Renders an SVG folder shape with a rounded rectangle body and top tab.
 * This component must be used within an AssetCardElement.
 *
 * The folder shape is constructed using three SVG paths:
 * 1. A blue stroke for activation/selection state
 * 2. A gradient background fill
 * 3. An inset grey stroke for keyboard focus
 *
 * The paths use clip-paths to ensure proper layering and visual effects.
 *
 * Try these tools for useful SVG explanations:
 *
 * - https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/d
 * - https://svg-path-visualizer.netlify.app
 * - https://svgviewer.dev
 */
export const FolderTabSvg = memo(function FolderTabSvgComponent({
  folderId,
  isOverlay,
  className,
  variant = 'default',
  ...otherProps
}: AssetCardFolderTabSvgProps) {
  const clipPathId = useId();
  const isColoredFolders = useIsLabsFeatureEnabled('folder_colors');
  const { colorToken } = useFolderColor(folderId ?? '');
  const themeName = useThemeName();
  const isLight = themeName === 'light' || themeName === 'light-accentNeutral';
  const folderColor =
    isColoredFolders && colorToken
      ? getFolderColorMix(colorToken, isLight)
      : undefined;
  const [folderDimensions, setFolderDimensions] = useState({
    width: 200,
    height: 200,
  });
  // Get dimension values based on selected variant
  const { radius, tabHeight, tabWidth, bezierX, bezierEndX } =
    DIMENSIONS[variant];
  // Generate SVG path for folder shape
  const path = useMemo(
    () =>
      getFolderTabPath({
        bezierEndX,
        bezierX,
        height: folderDimensions.height,
        radius,
        tabHeight,
        tabWidth,
        width: folderDimensions.width,
      }),
    [folderDimensions, radius, tabHeight, tabWidth, bezierX, bezierEndX]
  );

  /**
   * We need to use a ref to track the container dimensions because the folder is rendered as an absolute position element and we need to know its dimensions
   */
  const containerRef = useRef<HTMLDivElement | null>(null);
  /**
   * ResizeObserver is managed via a callback ref and a useEffect, ensuring only one observer is created and cleaned up properly.
   */
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setFolderDimensions((prev) =>
          prev.width !== width || prev.height !== height
            ? { width, height }
            : prev
        );
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <StyledFolderContainer
      ref={containerRef}
      style={
        isColoredFolders && colorToken
          ? ({
              '--folder-color': folderColor,
              '--folder-color-hover': getFolderColorMix(
                colorToken,
                isLight,
                isLight ? 50 : 30
              ),
            } as React.CSSProperties)
          : undefined
      }
    >
      <StyledFolderCardSvg
        className={className}
        fill={
          isOverlay
            ? undefined
            : folderColor ?? theme.colors.components.folder.header.background
        }
        {...otherProps}
      >
        <defs>
          <clipPath id={clipPathId}>
            <path d={path} strokeWidth={0} />
          </clipPath>
        </defs>
        {!isOverlay && (
          <>
            <path d={path} fillOpacity={0} />
            <path d={path} strokeOpacity={0} />
          </>
        )}
        <path clipPath={`url(#${clipPathId})`} d={path} fillOpacity={1} />
      </StyledFolderCardSvg>

      {!isOverlay && (
        <StyledBackground $radius={radius}>
          <StyledBackgroundShadow
            $folderColor={folderColor}
            $radius={radius}
            $tabHeight={tabHeight}
            $variant={variant}
          />
        </StyledBackground>
      )}
    </StyledFolderContainer>
  );
});

FolderTabSvg.displayName = 'FolderTabSvg';

/**
 * Creates the SVG path for the folder tab.
 */
function getFolderTabPath({
  bezierEndX,
  bezierX,
  height,
  radius,
  tabHeight,
  tabWidth,
  width,
}: {
  bezierEndX: number;
  bezierX: number;
  height: number;
  radius: number;
  tabHeight: number;
  tabWidth: number;
  width: number;
}) {
  return `
    M 0,${tabHeight}
    L 0,${radius}
    C 0,${radius / 2} ${radius / 2},0 ${radius},0
    L ${tabWidth * (2 / 3)},0
    c ${bezierX},0 ${bezierX},${tabHeight / 2} ${bezierEndX},${tabHeight / 2}
    H ${width - radius}
    a ${radius},${radius} 0 0 1 ${radius},${radius}
    V ${height - radius}
    a ${radius},${radius} 0 0 1 ${-radius},${radius}
    H ${radius}
    a ${radius},${radius} 0 0 1 ${-radius},${-radius}
    Z
  `
    .replace(/\n\s+/gm, ' ')
    .trim();
}

export const StyledFolderCardSvg = styled.svg`
  height: 100%;
  left: 0;
  overflow: visible;
  pointer-events: none;
  position: absolute;
  top: 0;
  width: 100%;
`;

const StyledFolderContainer = styled.div`
  height: 100%;
  left: 0;
  overflow: visible;
  pointer-events: none;
  position: absolute;
  top: 0;
  width: 100%;
`;

type StyledBackgroundProps = {
  $radius: number;
};

const StyledBackground = styled.div<StyledBackgroundProps>`
  border-radius: ${(p) => p.$radius}px;
  height: 100%;
  left: 0;
  overflow: clip;
  position: absolute;
  top: 0;
  width: 100%;
`;

/**
 * A component to fill in the folder and accent shape.
 * It is used to create the shadow effect on the folder.
 * It is also used to create the background color of the folder based on the variant and the viewport.
 */

type StyledBackgroundShadowProps = StyledBackgroundProps & {
  $tabHeight: number;
  $variant: Required<AssetCardFolderTabSvgProps>['variant'];
  $folderColor?: string;
};

export const StyledBackgroundShadow = styled.div<StyledBackgroundShadowProps>`
  background-color: ${({ $variant, $folderColor }) =>
    $folderColor ??
    ($variant === 'compact'
      ? theme.colors.components.folder.header.background
      : ASSET_BG_COLOR)};
  border-color: ${({ $folderColor }) =>
    $folderColor
      ? `color-mix(in srgb, ${$folderColor} 40%, transparent)`
      : theme.colors.components.folder.border};
  border-radius: ${({ $radius }) => ($radius ? `${$radius}px` : null)};
  border-style: solid;
  border-width: ${({ $variant }) => ($variant === 'compact' ? '0.5px' : '1px')};
  box-shadow: ${({ $variant }) =>
    $variant === 'compact' ? smallFolderShadow : theme.card.folder};
  height: calc(100% - ${({ $tabHeight }) => $tabHeight}px);
  left: 0;
  position: absolute;
  top: ${({ $tabHeight }) => $tabHeight}px;
  width: 100%;

  @media (min-width: ${theme.breakpoints.tablet}) {
    background-color: ${({ $variant, $folderColor }) =>
      $folderColor ??
      ($variant === 'compact'
        ? theme.colors.background.primary
        : ASSET_BG_COLOR)};
  }
`;

// Shadow styles for compact variant
const smallFolderShadow = `
  0px -1px 2px rgba(0, 0, 0, 0.05),
  0px -2px 4px rgba(0, 0, 0, 0.08),
  0px -3px 6px rgba(0, 0, 0, 0.12);
`;
